import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";

const inputVariants = cva(
  "flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
  {
    variants: {
      variant: {
        default: "border-input bg-background focus-visible:ring-ring",
        error: "border-[hsl(var(--status-error))] bg-background focus-visible:ring-[hsl(var(--status-error))]",
        success: "border-[hsl(var(--status-success))] bg-background focus-visible:ring-[hsl(var(--status-success))]",
        warning: "border-[hsl(var(--status-warning))] bg-background focus-visible:ring-[hsl(var(--status-warning))]",
      },
      size: {
        default: "h-10",
        sm: "h-9",
        lg: "h-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface EnhancedInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  success?: string;
  warning?: string;
  helpText?: string;
  showPasswordToggle?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClear?: () => void;
}

const EnhancedInput = React.forwardRef<HTMLInputElement, EnhancedInputProps>(
  ({
    className,
    variant,
    size,
    type = "text",
    label,
    error,
    success,
    warning,
    helpText,
    showPasswordToggle = false,
    leftIcon,
    rightIcon,
    onClear,
    ...props
  }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // Determine variant based on validation state
    const computedVariant = error ? "error" : success ? "success" : warning ? "warning" : variant;

    // Determine the actual input type
    const inputType = type === "password" && showPassword ? "text" : type;

    const handleTogglePassword = () => {
      setShowPassword(!showPassword);
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      }
    };

    return (
      <div className="space-y-2">
        {label && (
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
              {leftIcon}
            </div>
          )}
          
          <input
            type={inputType}
            className={cn(
              inputVariants({ variant: computedVariant, size, className }),
              leftIcon && "pl-10",
              (rightIcon || showPasswordToggle || onClear) && "pr-10",
              isFocused && "ring-2 ring-offset-2"
            )}
            ref={ref}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            {type === "password" && showPasswordToggle && (
              <button
                type="button"
                onClick={handleTogglePassword}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            )}
            
            {onClear && props.value && (
              <button
                type="button"
                onClick={handleClear}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                ×
              </button>
            )}
            
            {rightIcon && (
              <div className="text-muted-foreground">
                {rightIcon}
              </div>
            )}
            
            {/* Status icons */}
            {error && <AlertCircle className="h-4 w-4 text-[hsl(var(--status-error))]" />}
            {success && <CheckCircle className="h-4 w-4 text-[hsl(var(--status-success))]" />}
            {warning && <AlertCircle className="h-4 w-4 text-[hsl(var(--status-warning))]" />}
          </div>
        </div>
        
        {/* Help text, error, success, or warning messages */}
        {(error || success || warning || helpText) && (
          <div className="text-sm">
            {error && (
              <p className="text-[hsl(var(--status-error))] flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
            {success && (
              <p className="text-[hsl(var(--status-success))] flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                {success}
              </p>
            )}
            {warning && (
              <p className="text-[hsl(var(--status-warning))] flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {warning}
              </p>
            )}
            {helpText && !error && !success && !warning && (
              <p className="text-muted-foreground">{helpText}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);
EnhancedInput.displayName = "EnhancedInput";

export { EnhancedInput, inputVariants };
